import React, { useEffect, useState } from "react";
import {
    Box, CircularProgress,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { Dialog } from "@mui/material";
import AccountPickerGroup, {
    AccountPickerGroupValue
} from "../../widgets/AccountPicker/AccountPickerGroup";
import {
    AccountAction,
    AccountAddress,
    CartCheckoutItem,
    useCheckoutCartMutation,
    useGetAccountQuery,
} from "../../../services/grace";
import {
    useGridApiRef,
    GridRowId,
    GridSelectionModel,
    DataGridPro
} from "@mui/x-data-grid-pro";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { setSelectionModel } from "../../../features/inventory/inventorySlice";
import { useAlertDispatch } from "../../../context/AlertContext/AlertContext";
import GDGAppBarButton from "../../app/GDGAppBar/GDGAppBarButton";
import GDGAppBar from "../../app/GDGAppBar/GDGAppBar";
import { DialogProps } from "@mui/material/Dialog/Dialog";
//redux
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { setSearchCheckoutFormText, setSelectedValuesCheckoutForm, setOptionsCheckout } from "../../../features/search/searchSlice";

interface AddressValues {
    contact: string;
    line1: string;
    line2: string;
    line3: string;
    phone: string;
}

export interface CartCheckoutDialogProps extends Omit<DialogProps, "onClose" | "onSubmit"> {
    count: number;
    onClose: () => void;
    onSubmit: (id: string) => void;
}

export default function CartCheckoutDialog(props: CartCheckoutDialogProps): JSX.Element {
    const {
        count,
        onClose,
        onSubmit,
        ...other
    } = props;

    const dispatch = useAppDispatch();
    const apiRef = useGridApiRef();
    const alertDispatch = useAlertDispatch();
    const selectionModel = useAppSelector((state) => state.cart.selectionModel);

    const [formValues, setFormValues] = useState<CartCheckoutItem>({
        accountLevel1: "",
        accountLevel2: "",
        accountLevel3: "",
        priority: "",
        addressId: "",
        notes: "",
    });

    const [addressValues, setAddressValues] = useState<AddressValues>({
        contact: "",
        line1: "",
        line2: "",
        line3: "",
        phone: "",
    });

    const [selectedAccount, setSelectedAccount] = useState("");

    const {
        data,
        error,
        isError,
        isFetching,
    } = useGetAccountQuery(selectedAccount, { skip: selectedAccount === "" });

    const [addressGridData, setAddressGridData] = useState<Array<AccountAddress>>([]);
    const [actionCodes, setActionCodes] = useState<Array<AccountAction>>([]);

    const [inProgress, setInProgress] = useState(false);

    const [checkoutCart] = useCheckoutCartMutation();

    const [accountValues, setAccountValues] = useState<AccountPickerGroupValue>({
        level1: "",
        level2: "",
        level3: "",
    });

    const selectedValues = useAppSelector((state) => state.search.selectedValuesCheckoutForm);

    useEffect(() => {
        // Clear address data as it's now invalid (include the
        // selectionModel as that's not cleared when the grid reloads
        setSelectionModel([]);
        setActionCodes([]);

        setFormValues(formValues => {
            return {
                ...formValues,
                addressId: "",
            };
        });

        if (accountValues.level3 !== "") {
            setSelectedAccount(accountValues.level3);
        } else if (accountValues.level2 !== "") {
            setSelectedAccount(accountValues.level2);
        } else if (accountValues.level1 !== "") {
            setSelectedAccount(accountValues.level1);
        } else {
            setAddressGridData([]);
            setAddressValues({
              contact: "",
              line1: "",
              line2: "",
              line3: "",
              phone: "",
            });
        }
    }, [accountValues]);

    useEffect(() => {
        if (selectedValues) {
            setAccountValues({
                level1: selectedValues.level1,
                level2: selectedValues.level2,
                level3: selectedValues.level3,
            });
          }
    },[selectedValues])
    
    useEffect(() => {
        if (isFetching) {
            setAddressGridData([]);
            setActionCodes([]);
        } else if (isError) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: "add",
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: "add",
                    payload: {
                        type: "general",
                        message: "Failed to load address data",
                    }
                });
            }
        } else if (data !== undefined && data.addresses !== undefined) {
            setAddressGridData(data.addresses);
            setActionCodes(data.actionCodes !== undefined ? data.actionCodes : []);
        }
    }, [data, isFetching, isError, error, alertDispatch]);

    const resetForm = () => {
        // Clear address data as it's now invalid (include the
        // selectionModel as that's not cleared when the grid reloads)
        setSelectionModel([]);
        setAddressGridData([]);
        setActionCodes([]);
        setAddressValues({
            contact: "",
            line1: "",
            line2: "",
            line3: "",
            phone: "",
        });

        setAccountValues({
            level1: "",
            level2: "",
            level3: "",
        });

        setFormValues({
            accountLevel1: "",
            accountLevel2: "",
            accountLevel3: "",
            priority: "",
            addressId: "",
            notes: "",
        })
    }

    const handleClose = () => {
        dispatch(setOptionsCheckout(null));
        dispatch(setSearchCheckoutFormText(null));
        dispatch(setSelectedValuesCheckoutForm(null));
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        const accountForm = {
            ...formValues,
            accountLevel1: accountValues.level1,
            accountLevel2: accountValues.level2,
            accountLevel3: accountValues.level3,
        }

        const cartItems = selectionModel;
        const cartItemsIds = [];
        let cartForm;

        if (cartItems.length > 0) {
          for (let i = 0; i < cartItems.length; i++) {
            const cartId = { id: cartItems[i] };
            cartItemsIds.push(cartId);
          }
          cartForm = { items: cartItemsIds };
        }
        const checkoutData = { ...accountForm, ...cartForm };

        if (accountValues.level1 === "" || formValues.priority === "") {
            return;
        }

        try {
            setInProgress(true);
            const response = await checkoutCart(checkoutData).unwrap();
            if (response === undefined || response.receiptId === undefined) {
                alertDispatch({
                    type: "add",
                    payload: {
                        type: "general",
                        message: "Failed to check cart out",
                    }
                });
                setInProgress(false);
            } else {
                resetForm();
                onSubmit(response.receiptId);

            }
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: "add",
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: "add",
                    payload: {
                        type: "general",
                        message: "Failed to check cart out",
                    }
                });
            }
        }

        setInProgress(false);
    };

    const columns = [
        { field: "id", headerName: "ID", width: 120, hide: true },
        { field: "description", headerName: "Description", width: 200 },
        { field: "contact", headerName: "Onsite Contact Name", width: 180 },
        { field: "line1", headerName: "Suite/Level/Building/Unit Number/Letter", width: 200 },
        { field: "line2", headerName: "Number, Street Name, Street Type", width: 200 },
        { field: "line3", headerName: "Suburb, State, Postcode", width: 200 },
        { field: "phone", headerName: "Contact Phone", width: 150 },
    ];

    const getCellValue = (id: GridRowId, field: string) => {
        const value = apiRef.current.getCellValue(id, field);
        if (value === undefined || value === null) {
            return "";
        }

        return value.toString();
    }

    const handleAccountChange = (value: AccountPickerGroupValue) => {
        setAccountValues({
            level1: value.level1,
            level2: value.level2,
            level3: value.level3,
        });
        setFormValues({
            ...formValues,
            priority: "",
        })
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value,
        })
    };

    const handleSelectionModelChange = (model: GridSelectionModel) => {
        if (inProgress || model.length !== 1) {
            return;
        }

        const id = model[0];

        setFormValues({
            ...formValues,
            addressId: id.toString(),
        });

        setAddressValues({
            contact: getCellValue(id, "contact"),
            line1: getCellValue(id, "line1"),
            line2: getCellValue(id, "line2"),
            line3: getCellValue(id, "line3"),
            phone: getCellValue(id, "phone"),
        });
    }

    const addressError = addressValues.contact === ""
        && addressValues.line1 === ""
        && addressValues.line2 === ""
        && addressValues.line3 === ""
        && addressValues.phone === "";

    const textStyle = {
        margin: 1,
        width: "90%",
    }

    return (
        <Dialog
            fullScreen
            onClose={handleClose}
            {...other}
        >
            <GDGAppBar title={`Check out web order (# of items: ${count})`} onClose={handleClose} drawerWidth={0}>
                {inProgress &&
                    <CircularProgress color="secondary" size={20}/>
                }
                <GDGAppBarButton
                    onClick={handleSubmit}
                    text="Check Out"
                    disabled={inProgress}
                />
            </GDGAppBar>
            <Grid
                container
                alignItems="flex-start"
                justifyContent="space-evenly"
                sx={{
                    spacing: 1,
                    margin: 1,
                    width: "98%",
                }}
            >
                <Grid item xs={9}>
                    <AccountPickerGroup
                        type={"checkout"}
                        disabled={inProgress}
                        label="Accounts"
                        required
                        value={accountValues}
                        onChange={handleAccountChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Grid container sx={{
                        spacing: 1,
                        width: "98%",
                    }}>
                        <Grid item xs={12}>
                            <Typography variant={"subtitle2"} sx={{ marginLeft: 1, marginTop:4 }}>Service Priority</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="priority"
                                variant="outlined"
                                sx={ textStyle }
                                select
                                required
                                error={formValues.priority === ""}
                                disabled={inProgress || accountValues.level1 === ""}
                                value={formValues.priority}
                                onChange={handleChange}
                            >
                                {actionCodes?.map((action) => (
                                    <MenuItem key={action.code} value={action.code}>
                                        {action.description}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4}>
                    <Grid container sx={{ height: "400px", maxHeight: "400px" }}>
                        <Grid container item xs={12}>
                            <Grid item xs={12}>
                                <Typography variant={"subtitle2"}>Address</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="contact"
                                    variant="standard"
                                    sx={ textStyle }
                                    required
                                    disabled
                                    error={addressError}
                                    label="Onsite Contact Name"
                                    value={addressValues.contact}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="line1"
                                    variant="standard"
                                    sx={ textStyle }
                                    required
                                    disabled
                                    error={addressError}
                                    label="Suite/Level/Building/Unit Number/Letter"
                                    value={addressValues.line1}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="line2"
                                    variant="standard"
                                    sx={ textStyle }
                                    required
                                    disabled
                                    error={addressError}
                                    label="Number, Street Name, Street Type"
                                    value={addressValues.line2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="line3"
                                    variant="standard"
                                    sx={ textStyle }
                                    required
                                    disabled
                                    error={addressError}
                                    label="Suburb, State, Postcode"
                                    value={addressValues.line3}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="phone"
                                    variant="standard"
                                    sx={ textStyle }
                                    required
                                    disabled
                                    error={addressError}
                                    label="Contact Phone"
                                    value={addressValues.phone}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={8}>
                    <Box sx={{flexGrow: 1}}>
                        <DataGridPro
                            apiRef={apiRef}
                            sx={{ height: "400px", maxHeight: "400px" }}
                            columns={columns}
                            rows={addressGridData}
                            disableMultipleSelection
                            disableColumnMenu
                            disableColumnReorder
                            onSelectionModelChange={handleSelectionModelChange}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 1 }}>
                    <TextField
                        name="notes"
                        variant="outlined"
                        sx={{ width: "100%" }}
                        disabled={inProgress}
                        label="Notes"
                        multiline
                        rows={8}
                        value={formValues.notes}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}
